// ─────────────────────────────────────────────────────────────────
// Glass-i — Accounts module API client (Stage 6B.1).
//
// Thin axios wrappers around /api/erp/accounts/*. Mirrors the
// customerApi shape (translateQuery for camelCase → snake_case)
// so grid grouping / filtering / sorting work out of the box.
// ─────────────────────────────────────────────────────────────────

import client from '../../../api/client';

const BASE = '/api/erp/accounts';

function unwrap(resp) {
  return resp && resp.data && resp.data.data !== undefined
    ? resp.data.data
    : resp && resp.data;
}

// camelCase → snake_case bridge. Same purpose as customerApi's
// GRID_FIELD_TO_SQL — the reusable grid emits dataIndex verbatim
// but the backend whitelist keys on snake_case columns.
const GRID_FIELD_TO_SQL = {
  // Shared
  accountCode:    'account_code',
  accountName:    'account_name',
  accountType:    'account_type',
  accountGroup:   'account_group',
  // Stage 6B.9B — subtype filter/sort/group support.
  accountSubtype: 'account_subtype',
  normalBalance:  'normal_balance',
  isSystem:       'is_system',
  isActive:       'is_active',
  gstRelevant:    'gst_relevant',
  taxCode:        'tax_code',
  taxName:        'tax_name',
  taxType:        'tax_type',
  hsnSac:         'hsn_sac',
  isRcm:          'is_rcm',
  effectiveFrom:  'effective_from',
  effectiveTo:    'effective_to',
  financialYear:  'financial_year',
  periodName:     'period_name',
  periodType:     'period_type',
  periodStart:    'period_start',
  periodEnd:      'period_end',
  status:         'status',
  gstReturnPeriod: 'gst_return_period',
  documentType:    'document_type',
  prefix:          'prefix',
  resetFrequency:  'reset_frequency'
};

function mapField(c) {
  return c && typeof c === 'string' ? (GRID_FIELD_TO_SQL[c] || c) : c;
}
function translateQuery(q) {
  if (!q || typeof q !== 'object') return q;
  const out = { ...q };
  if (Array.isArray(out.filters)) {
    out.filters = out.filters.filter((f) => f && f.field)
      .map((f) => ({ ...f, field: mapField(f.field) }));
  }
  if (Array.isArray(out.sort)) {
    out.sort = out.sort.filter((s) => s && s.field)
      .map((s) => ({ ...s, field: mapField(s.field) }));
  }
  if (typeof out.groupBy === 'string' && out.groupBy) out.groupBy = mapField(out.groupBy);
  if (out.advancedFilters && Array.isArray(out.advancedFilters.rules)) {
    out.advancedFilters = {
      ...out.advancedFilters,
      rules: mapRules(out.advancedFilters.rules)
    };
  }
  return out;
}
function mapRules(rules) {
  return rules.map((r) => {
    if (!r || typeof r !== 'object') return r;
    if (Array.isArray(r.rules)) return { ...r, rules: mapRules(r.rules) };
    if (typeof r.field === 'string' && r.field) return { ...r, field: mapField(r.field) };
    return r;
  });
}

export const accountsApi = {
  // ledgers
  listLedgers: (q) => client.post(`${BASE}/ledgers/list`, translateQuery(q) || {}).then(unwrap),
  getLedger:   (id) => client.get(`${BASE}/ledgers/${encodeURIComponent(id)}`).then(unwrap),
  createLedger: (payload) => client.post(`${BASE}/ledgers`, payload).then(unwrap),
  updateLedger: (id, payload) =>
    client.put(`${BASE}/ledgers/${encodeURIComponent(id)}`, payload).then(unwrap),
  // Stage 6B.9B — lookup ledgers by account_subtype. Accepts a single
  // subtype or a comma-separated string (e.g. 'bank,cash'). Used by
  // Contra Voucher, future Fixed Assets, future Investments.
  ledgersBySubtype: (subtype, { q, limit = 100 } = {}) =>
    client.get(`${BASE}/ledgers/lookups/by-subtype`, {
      params: {
        subtype: Array.isArray(subtype) ? subtype.join(',') : subtype,
        ...(q ? { q } : {}),
        limit
      }
    }).then(unwrap),

  // tax rates
  listTaxRates: (q) => client.post(`${BASE}/tax-rates/list`, translateQuery(q) || {}).then(unwrap),
  getTaxRate:   (id) => client.get(`${BASE}/tax-rates/${encodeURIComponent(id)}`).then(unwrap),
  createTaxRate: (payload) => client.post(`${BASE}/tax-rates`, payload).then(unwrap),
  updateTaxRate: (id, payload) =>
    client.put(`${BASE}/tax-rates/${encodeURIComponent(id)}`, payload).then(unwrap),

  // periods
  listPeriods: (q) => client.post(`${BASE}/periods/list`, translateQuery(q) || {}).then(unwrap),
  getPeriod:   (id) => client.get(`${BASE}/periods/${encodeURIComponent(id)}`).then(unwrap),
  createPeriod: (payload) => client.post(`${BASE}/periods`, payload).then(unwrap),
  updatePeriod: (id, payload) =>
    client.put(`${BASE}/periods/${encodeURIComponent(id)}`, payload).then(unwrap),

  // GST reports
  gstr1Summary:  (params) => client.get(`${BASE}/gst/reports/gstr1-summary`,  { params }).then(unwrap),
  gstr3bSummary: (params) => client.get(`${BASE}/gst/reports/gstr3b-summary`, { params }).then(unwrap),
  hsnSummary:    (params) => client.get(`${BASE}/gst/reports/hsn-summary`,    { params }).then(unwrap),
  itcSummary:    (params) => client.get(`${BASE}/gst/reports/itc-summary`,    { params }).then(unwrap),
  gstPayable:    (params) => client.get(`${BASE}/gst/reports/gst-payable`,    { params }).then(unwrap),

  // Stage 6B.2 — Setup readiness
  getSetupStatus: () => client.get(`${BASE}/setup/status`).then(unwrap),

  // Stage 6B.2 — Period generator
  generatePeriods:  (payload) =>
    client.post(`${BASE}/periods/generate`, payload).then(unwrap),
  generateQuarters: (payload) =>
    client.post(`${BASE}/periods/generate-quarters`, payload).then(unwrap),

  // Stage 6B.2 — Document sequences
  listDocumentSequences: (q) =>
    client.post(`${BASE}/document-sequences/list`, translateQuery(q) || {}).then(unwrap),
  updateDocumentSequence: (id, payload) =>
    client.put(`${BASE}/document-sequences/${encodeURIComponent(id)}`, payload).then(unwrap),
  ensureDocumentSequences: (payload) =>
    client.post(`${BASE}/document-sequences/ensure`, payload || {}).then(unwrap),
  // Read-only preview for the Renumber dialog: the current number, the highest
  // number a live document actually holds, and the lowest safe value.
  documentSequenceUsage: (id) =>
    client.get(`${BASE}/document-sequences/${encodeURIComponent(id)}/usage`).then(unwrap),
  // MASTER ONLY. Winds current_number back so a deleted document's number is
  // reused. The server refuses any value below the highest live number and
  // returns 409 DOC_SEQUENCE_NUMBER_IN_USE naming what is in the way.
  renumberDocumentSequence: (id, currentNumber) =>
    client.post(`${BASE}/document-sequences/${encodeURIComponent(id)}/renumber`,
      { currentNumber }).then(unwrap),

  // Stage 6B.2 — Accounting settings
  getSettings:    () => client.get(`${BASE}/settings`).then(unwrap),
  updateSettings: (payload) => client.put(`${BASE}/settings`, payload).then(unwrap),

  // Stage 6B.2 — Opening balances
  listOpeningBalances:   () => client.get(`${BASE}/opening-balances`).then(unwrap),
  updateOpeningBalances: (payload) =>
    client.put(`${BASE}/opening-balances`, payload).then(unwrap)
};

export default accountsApi;
